import { IconButton } from '@civilgrid/ui/components';
import { useMutation } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import type { Row, Table } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { printProjectPDF as apiPrintProjectPDF } from '~/api/projects2';
import { createCsvDataFromTable } from '~/common/exportUtils';
import { downloadCSV } from '~/common/miscUtils';
import { notifyExportError } from '~/common/notifyExportError';
import { notifyExportQueued } from '~/common/notifyExportQueued';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project } from '~/common/types';
import { Dropdown, type Item as DropdownItem } from '~/components2/Dropdown';
import { SendEmailModal } from '~/components2/SendEmailModal';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '..';
import store, { type ParcelDetails } from './store';

export default function ExportComponent({
  project,
  table,
  disabled = false,
}: {
  project?: Project;
  table: Table<ParcelDetails>;
  disabled?: boolean;
}) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const [pdfConfirmOpen, setPdfConfirmOpen] = useState(false);

  const dispatchEvent = (custom: Record<string, string>, ccRecipientCount?: number) => {
    if (projectId) {
      captureTypedPosthogEvent(POSTHOG_EVENTS.projectExportInitiated, {
        project_id: projectId,
        organization_id: rootStore.currentOrganizationId || undefined,
        export_type: custom.type || 'unknown',
        source: 'land_rights',
        cc_recipient_count: ccRecipientCount,
      });
    }
  };

  const pdfMutation = useMutation({
    mutationKey: ['land-rights-pdf', { projectId }],
    mutationFn: (ccEmails?: string[]) =>
      apiPrintProjectPDF(projectId, 'landRights', Reflect.get(store, 'parcelDetails'), [], ccEmails),
    onSuccess: (_data, ccEmails) => {
      notifyExportQueued({
        title: 'PDF Download',
        message:
          'Your PDF download has started. You will receive the PDF report in your email in the next few minutes.',
        ccEmails,
      });

      dispatchEvent({ type: 'pdf' }, ccEmails?.length ?? 0);
    },
    onError: notifyExportError,
  });

  const onExportCSV = (rows: Row<ParcelDetails>[], selection: 'all' | 'selected') => {
    if (!rows.length) return;
    const columns = table.getAllLeafColumns();
    const csvData = createCsvDataFromTable({ rows, columns });
    downloadCSV(csvData, `${project!.name}_Parcel_Details.csv`);
    dispatchEvent({ type: 'csv', selection });
  };

  const selectedRows = table.getSelectedRowModel().rows;
  const allRows = table.getCoreRowModel().rows;

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const dropdownItems = useMemo<DropdownItem[]>(() => {
    return [
      {
        type: 'simple',
        label: 'PDF',
        onClick: () => setPdfConfirmOpen(true),
      },
      { type: 'separator' },
      {
        type: 'subMenu',
        label: 'CSV',
        items: [
          {
            type: 'simple',
            label: 'Selected Rows',
            isDisabled: !selectedRows.length,
            onClick: () => onExportCSV(selectedRows, 'selected'),
          },
          {
            type: 'simple',
            label: 'All Rows',
            isDisabled: !allRows.length,
            onClick: () => onExportCSV(allRows, 'all'),
          },
        ],
      },
    ];
  }, [selectedRows, allRows, project]);

  return (
    <>
      <Dropdown
        triggerElement={
          <IconButton icon="download" variant="primary" data-testid="land-rights-export-button" disabled={disabled}>
            Export
          </IconButton>
        }
        disabled={disabled}
        items={dropdownItems}
      />
      <SendEmailModal
        open={pdfConfirmOpen}
        onOpenChange={setPdfConfirmOpen}
        title="Export parcel details"
        testIdPrefix="land-rights-export"
        onSend={(ccEmails) => pdfMutation.mutateAsync(ccEmails.length > 0 ? ccEmails : undefined)}
      />
    </>
  );
}
